import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
	ApproveTemplateAsync,
	GetAwaitingApprovalTemplates,
	RejectTemplateAsync,
} from "../../api/batchNotificationController";
import { TemplateType } from "../../common/enum";
import { CircularProgress, Grid } from "../../common/MaterialComponent";
import Shared from "../../common/shared";
import { useGetComponentStringsContext } from "../../hooks/useGetComponentStrings";
import { PreSubmissionConfigurationModel } from "../../models/BatchMessage/BatchMessageDetailModel";
import { ConfirmationDialog } from "../Confirmation/ConfirmationDialog";
import { ErrorPage } from "../ErrorPage/ErrorPage";
import { TemplateListDataGrid } from "../TemplateListDataGrid/TemplateListDataGrid";

export const TemplatesAwaitingApproval: React.FC = () => {
	const [error, setError] = React.useState<string>("");
	const [templateList, setTemplateList] = React.useState<
		PreSubmissionConfigurationModel[]
	>([]);
	const [updateRequired, setUpdateRequired] = React.useState<boolean>(true);

	const [
		openConfirmationPopup,
		setOpenConfirmationPopup,
	] = React.useState<boolean>(false);
	const [
		selectedTemplate,
		setSelectedTemplate,
	] = React.useState<PreSubmissionConfigurationModel>(
		new PreSubmissionConfigurationModel()
	);
	const [mounted, setMounted] = React.useState<boolean>(false);
	const strings = useGetComponentStringsContext().TemplatesAwaitingApproval;
	const history = useHistory();

	useEffect(() => {
		const getAllTemplates = async () => {
			try {
				const res = await GetAwaitingApprovalTemplates();
				if (res.error) {
					setError(res.error);
				} else {
					setTemplateList(res.responseObj ? res.responseObj : []);
					setMounted(true);
					setUpdateRequired(false);
				}
			} catch (err) {
				setError(err);
			}
		};

		if (updateRequired) {
			setTimeout(() => {
				getAllTemplates();
			}, 1000);
		}
	}, [updateRequired]);

	const onNotificationIdChange = async (
		preSubmissionConfigurationDetail: PreSubmissionConfigurationModel
	) => {
		const pathName =
			preSubmissionConfigurationDetail.type ===
			TemplateType[TemplateType.BatchMessaging]
				? "/batchMessageConfiguration/"
				: "/requestForHelpConfiguration/";
		history.push({
			pathname:
				pathName +
				preSubmissionConfigurationDetail.partitionKey +
				"/" +
				preSubmissionConfigurationDetail.id,
			state: {
				requestFromPath: "templatesAwaitingApproval",
			},
		});
	};

	const approveTemplateAsync = async (
		preSubmissionConfigurationDetail: PreSubmissionConfigurationModel
	) => {
		try {
			const res = await ApproveTemplateAsync(
				preSubmissionConfigurationDetail.id,
				preSubmissionConfigurationDetail.partitionKey
			);
			if (!res.error) {
				deleteTemplateRecordFromList(preSubmissionConfigurationDetail.id);
			}
			Shared.showSnackbarMessage(
				res.error ? res.error : strings.approveTemplateSuccessMessage
			);
		} catch (err) {
			Shared.showSnackbarMessage(strings.approveTemplateErrorMessage);
		}
	};

	const rejectTemplateAsync = (
		preSubmissionConfigurationDetail: PreSubmissionConfigurationModel
	) => {
		setSelectedTemplate(preSubmissionConfigurationDetail);
		setOpenConfirmationPopup(true);
	};

	const onConfirmRejectTemplate = async (rejectTemplate: boolean = false) => {
		if (rejectTemplate) {
			try {
				const res = await RejectTemplateAsync(
					selectedTemplate.id,
					selectedTemplate.partitionKey
				);
				if (!res.error) {
					deleteTemplateRecordFromList(selectedTemplate.id);
				}
				Shared.showSnackbarMessage(
					res.error
						? strings.rejectTemplateErrorMessage
						: strings.rejectTemplateSuccessMessage
				);
				setOpenConfirmationPopup(false);
			} catch (err) {
				Shared.showSnackbarMessage(strings.rejectTemplateErrorMessage);
				setOpenConfirmationPopup(false);
			}
		} else {
			setOpenConfirmationPopup(false);
		}
	};

	const deleteTemplateRecordFromList = (id: string) => {
		const templateListItems: PreSubmissionConfigurationModel[] = [
			...templateList,
		];
		const index = templateListItems.findIndex(
			(x: PreSubmissionConfigurationModel) => x.id === id
		);
		if (index !== -1) {
			templateListItems.splice(index, 1);
		}
		setTemplateList(templateListItems);
	};
	return (
		<div>
			{!mounted && error ? (
				<ErrorPage message={strings.templateListErrorMessage} />
			) : !mounted ? (
				<div className="loader">
					<CircularProgress size={50} />
				</div>
			) : (
				<>
					<Grid
						container
						spacing={3}
						data-testid="templateAwaitingApprovalList"
					>
						<Grid item xs={6}></Grid>
						<Grid item xs={12}>
							<TemplateListDataGrid
								templateList={templateList}
								displayStatusColumn={false}
								scheduledTemplate={false}
								title={strings.title}
								onNotificationIdChange={(
									model: PreSubmissionConfigurationModel
								) => onNotificationIdChange(model)}
								onApproveTemplateSelectedId={(
									model: PreSubmissionConfigurationModel
								) => {
									approveTemplateAsync(model);
								}}
								onRejectTemplateSelectedId={(
									model: PreSubmissionConfigurationModel
								) => {
									rejectTemplateAsync(model);
								}}
								messageCancelEnabled={false}
								templateUpdateRequired={setUpdateRequired}
								setMounted={setMounted}
							/>
						</Grid>
					</Grid>
					<ConfirmationDialog
						open={openConfirmationPopup}
						message={strings.rejectTemplateConfirmationMessage}
						closeDialog={(isDeleteBtnPressed?: boolean) =>
							onConfirmRejectTemplate(isDeleteBtnPressed)
						}
						positiveActionText={strings.deleteButtonText}
						negativeActionText={strings.cancelButtonText}
					></ConfirmationDialog>
				</>
			)}
		</div>
	);
};
